// src/constants/activityConstants.js

import {
  PersonAdd,
  PersonRemove,
  Business,
  Delete,
  Edit,
  Settings,
  AddCircle,
  VerifiedUser,
  Web,
  DeleteOutline,
  AddBox,
  RemoveCircle,
  Update,
  Restore,
  Backup,
  SystemUpdateAlt,
  Storage,
  Speed,
  Extension,
  Https,
  Shield,
  InfoOutlined,
} from "@mui/icons-material";
import { planNamesId } from "./plansId";
import { getRoleDisplayText } from "./Roles";

// Activity Types
export const ORG_ACTIVITIES = {
  REMOVE_USER_FROM_ORG: { text: "User " },
  CREATE_ORG: { text: "Organization " },
  UPDATE_ORG_NAME: { text: "Organization has been updated to " },
  USER_UPDATED_INVITE: { text: "Invitation has been " },
  CHANGE_ORG_OWNER: { text: "Owner changed to " },
  UPDATE_USER_ROLE: { text: "User role has been updated to " },
  ADD_SUBSRIPTION_PLAN: { text: "License" },
  ADD_USER: { text: "User " },
  ADD_DOMAIN: { text: "Domain " },
  CREATE_WEB_SITE: { text: "Website " },
  DELETE_WEB_SITE: { text: "Website " },
  DELETE_DOMAIN: { text: "Domain " },
  RESTORE_INITIATED: { text: "Restore Initiated" },
  PLAN_NAME: { text: "Plan name changed to " },
  "WARNING_PLAN_OVERGAUGE\n": {
    text: "Your plan is overgauged. Please upgrade your plan.",
  },
  ONDEMAND_BACKUP: { text: "On-demand backup started for " },
};

export const USER_ACTIVITIES = {
  DELETED_ORGANIZATION: { text: "Organization " },
  CREATE_WEB_SITE: { text: "Website " },
  ADD_DOMAIN: { text: "Domain " },
  DELETE_DOMAIN: { text: "Domain " },
  DELETE_WEB_SITE: { text: "Website " },
  ADD_SUBSRIPTION_PLAN: { text: "License was added " },
  "WARNING_PLAN_OVERGAUGE\n": {
    text: "Your plan is overgauged. Please upgrade your plan.",
  },
  ONDEMAND_BACKUP: { text: "On-demand backup started for " },
};

// Activity Messages
export const getActivityMessage = (activity, allPlans) => {
  const { actionType, userName, payload } = activity;
  const parsedPayload = JSON.parse(payload || "{}") || {};
  const roleName = getRoleDisplayText(parseInt(parsedPayload?.roleId, 10));
  // site events only promise {siteId, siteName, apiVersion} (+ their own fields)
  const siteName = parsedPayload.siteName;
  const byUser = userName ? ` by ${userName}` : "";

  switch (actionType) {
    case "USER_UPDATED_INVITE":
      return `Invitation has been ${parsedPayload.newStatus.toLowerCase()} by ${userName}`;
    case "ADD_USER":
      const members = parsedPayload.OrganizationMembers || [];
      const emails = [];

      members.forEach((member) => {
        if (member.memberEmailId) {
          emails.push(member.memberEmailId);
        }
        // if (member.memberInfo?.emailId) {
        //   emails.push(member.memberInfo.emailId);
        // }
      });

      if (emails.length === 0) {
        return "No users to invite.";
      }

      if (emails.length === 1) {
        return `User ${emails[0]} is invited.`;
      }

      if (emails.length > 1) {
        const firstTwoEmails = emails.slice(0, 2).join(", ");
        const remainingCount = emails.length - 1;
        return `Users ${firstTwoEmails} and ${remainingCount} more were invited.`;
      }

      // return `Users ${emails.join(", ")} were invited.`;
      break;
    case "ADD_SUBSRIPTION_PLAN":
      const planId = parsedPayload?.plan?.planId || null;
      const planName = planNamesId(allPlans, planId);
      return `License ${planName} was added by ${userName}`;
    case "CREATE_ORG":
      return `Organization ${parsedPayload.orgName} was created by ${userName}`;
    case "REMOVE_USER_FROM_ORG":
      return `User ${parsedPayload.memberEmailId} was removed from the organization by ${userName}`;
    case "UPDATE_ORG_NAME":
      return `Organization name has been updated to ${activity.orgName} by ${userName}`;
    case "CHANGE_ORG_OWNER":
      return `Owner changed to ${parsedPayload.newOwnerEmailId}`;
    case "UPDATE_USER_ROLE":
      return `User role has been updated to ${roleName} for ${parsedPayload.memberEmailId} by ${userName}`;
    case "ADD_DOMAIN": {
      // v2 custom domains log every step under one type (status); v1 rows have none
      const { domainName, status } = parsedPayload;
      if (status === "PENDING_VALIDATION") {
        return `Custom domain ${domainName} was added to site ${siteName}${byUser} (waiting for DNS)`;
      }
      if (status === "LIVE") {
        return `Custom domain ${domainName} is live on site ${siteName}`;
      }
      if (status === "FAILED") {
        return `Custom domain setup for ${domainName} on site ${siteName} failed`;
      }
      if (status === "PRIMARY") {
        return `${domainName} is now the primary domain of site ${siteName}${byUser}`;
      }
      return `Domain ${domainName} was added by ${userName}`;
    }
    case "CREATE_WEB_SITE":
      return `Website ${parsedPayload.siteName} was created by ${userName}`;
    case "DELETE_WEB_SITE":
      return `Website ${parsedPayload.siteName} was deleted by ${userName}`;
    case "DELETE_DOMAIN":
      return `Domain ${parsedPayload.domainName} was deleted by ${userName}`;
    case "RESTORE_INITIATED":
      return `Restore initiated for ${parsedPayload.siteName} by ${userName}`;
    case "DELETED_ORGANIZATION":
      return `Organization ${parsedPayload.orgName} was deleted by ${userName}`;
    case "PLAN_NAME":
      return `Plan name changed to ${parsedPayload.newPlanName}`;
    case "ONDEMAND_BACKUP":
      // written when the backup starts; "Backup created" comes as a bell once it is ready
      return `On-demand backup started for ${siteName}${byUser}`;
    case "BACKUP_JOB_FAILED":
      return `Backup failed for ${siteName}`;
    case "RESTORE_ABORTED":
      return `Restore failed for ${siteName}`;
    case "WP_VERSION_UPDATE": {
      const { version, status } = parsedPayload;
      const toVersion = version ? ` to ${version}` : "";
      if (status === "UPDATED") {
        return version
          ? `${siteName} was updated to WordPress ${version}`
          : `WordPress was updated for ${siteName}`;
      }
      if (status === "FAILED") {
        return `WordPress update${toVersion} failed for ${siteName}`;
      }
      if (status === "STARTED") {
        return `WordPress update${toVersion} started for ${siteName}${byUser}`;
      }
      return `WordPress update${toVersion} for ${siteName}${byUser}`;
    }
    case "ADD_ON": {
      const { addon, status, addedGb } = parsedPayload;
      let label;
      if (addon === "STORAGE") {
        label = `Storage upgrade${addedGb ? ` (+${addedGb} GB)` : ""}`;
      } else if (addon === "PERFORMANCE") {
        label = "Performance upgrade";
      } else {
        // older add-on rows don't say which add-on
        return siteName
          ? `Add-on purchased for ${siteName}${byUser}`
          : `Add-on purchased${byUser}`;
      }
      const statusText = {
        STARTED: "started",
        COMPLETED: "completed",
        FAILED: "failed",
      }[status];
      return statusText
        ? `${label} ${statusText} for ${siteName}`
        : `${label} for ${siteName}${byUser}`;
    }
    case "ENABLED_SSL":
      return `SSL was enabled for ${
        parsedPayload.domainName || siteName || "a domain"
      }${byUser}`;
    case "WAF_UPDATE":
      return `WAF settings were updated for ${siteName}${byUser}`;
    case "WARNING_PLAN_OVERGAUGE\n":
      return `Your plan is overgauged. Please upgrade your plan.`;
    default:
      // no wording of its own: the action type's description, so the row is never blank
      return `${activity.actionTypeDesc || actionType}${
        siteName ? ` for ${siteName}` : ""
      }${byUser}`;
  }
};

const parseActivityPayload = (payload) => {
  try {
    return JSON.parse(payload || "{}") || {};
  } catch (e) {
    return {};
  }
};

//Activity icons
// payload: the activity's payload JSON; WordPress updates and add-ons log every step under one type
export const getIconForActivity = (actionType, payload) => {
  const { addon, status } = parseActivityPayload(payload);
  if (actionType === "ADD_ON") {
    const color = status === "FAILED" ? "error" : "success";
    if (addon === "STORAGE") {
      return <Storage color={color} variant="outlined" />;
    }
    if (addon === "PERFORMANCE") {
      return <Speed color={color} variant="outlined" />;
    }
  }
  if (actionType === "WP_VERSION_UPDATE" && status === "FAILED") {
    return <SystemUpdateAlt color="error" variant="outlined" />;
  }
  // v2 custom domains: the certificate is the news once it is live / primary
  if (actionType === "ADD_DOMAIN" && status === "FAILED") {
    return <AddBox color="error" variant="outlined" />;
  }
  if (
    actionType === "ADD_DOMAIN" &&
    (status === "LIVE" || status === "PRIMARY")
  ) {
    return <Https color="success" variant="outlined" />;
  }
  return ACTIVITY_ICONS_COLOR[actionType] || DEFAULT_ACTIVITY_ICON;
};

export const DEFAULT_ACTIVITY_ICON = (
  <InfoOutlined color="primary" variant="outlined" />
);

// Activity Icons
export const ACTIVITY_ICONS_COLOR = {
  REMOVE_USER_FROM_ORG: <PersonRemove color="error" variant="outlined" />,
  ADD_USER: <PersonAdd color="success" variant="outlined" />,
  CREATE_ORG: <Business color="primary" variant="outlined" />,
  DELETED_ORGANIZATION: <Delete color="error" variant="outlined" />,
  UPDATE_ORG_NAME: <Edit color="primary" variant="outlined" />,
  CHANGE_ORG_OWNER: <Settings color="primary" variant="outlined" />,
  ADD_SUBSRIPTION_PLAN: <AddCircle color="success" variant="outlined" />,
  UPDATE_USER_ROLE: <VerifiedUser color="primary" variant="outlined" />,
  CREATE_WEB_SITE: <Web color="primary" variant="outlined" />,
  ONDEMAND_BACKUP: <Backup color="primary" variant="outlined" />,
  BACKUP_JOB_FAILED: <Backup color="error" variant="outlined" />,
  DELETE_WEB_SITE: <DeleteOutline color="error" variant="outlined" />,
  ADD_DOMAIN: <AddBox color="success" variant="outlined" />,
  DELETE_DOMAIN: <RemoveCircle color="error" variant="outlined" />,
  USER_UPDATED_INVITE: <Update color="primary" variant="outlined" />,
  RESTORE_INITIATED: <Restore color="secondary" variant="outlined" />,
  RESTORE_ABORTED: <Restore color="error" variant="outlined" />,
  WP_VERSION_UPDATE: <SystemUpdateAlt color="primary" variant="outlined" />,
  ADD_ON: <Extension color="success" variant="outlined" />,
  ENABLED_SSL: <Https color="success" variant="outlined" />,
  WAF_UPDATE: <Shield color="primary" variant="outlined" />,
  "WARNING_PLAN_OVERGAUGE\n": <AddCircle color="success" variant="outlined" />,
};
